import { forwardRef, useEffect, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { InputFieldWithCurrency } from '@/modules/common';
import { DoubleSidedSlider } from '@/modules/common/components/molecules/double-sided-slider';
import { ToggleWithDescription } from '@/modules/common/components/molecules/toggle-with-description';
import {
  BaseFeatureTab,
  BaseTabProps,
  FeatureMode,
  FeatureTabRef,
  SubmitDateRange,
  useTabFormState,
} from '@/modules/common/components/templates/feature-details';
import { ResolveConflictsModal } from '@/modules/projects/components/modals/resolve-conflicts-modal';
import { useCheckTeamMemberCapacityConflicts } from '@/modules/team/hooks/capacity/use-check-team-member-capacity-conflicts';
import { useTeamMemberChangeConflictResolution } from '@/modules/team/hooks/use-team-member-change-conflict-resolution';
import {
  teamMemberCapacityConflictFields,
  TeamMemberCapacityFieldKey,
} from '@/modules/team/utils/change-conflicts/team-member-capacity-conflict-config';
import { useTeamMemberCapacitySyncRules } from '@/modules/team/utils/change-conflicts/team-member-capacity-sync-rules';
import { compareFormValues } from '@/utils/form-state.utils';

import { MemberStatus, TeamMember, TeamMemberFormData } from '../../../types';
import {
  TeamMemberCapacityFormData,
  teamMemberCapacitySchema,
} from '../../../validations/team-member-capacity-schema';

export interface TeamMemberCapacityTabProps extends BaseTabProps<TeamMember, TeamMemberFormData> {
  /**
   * Callback to directly save data to API after conflict resolution
   */
  onDirectSave?: (
    data: TeamMemberFormData,
    effectiveStartDate?: Date,
    effectiveEndDate?: Date | null
  ) => Promise<void>;
}

export interface TeamMemberCapacityTabRef extends FeatureTabRef<TeamMemberFormData> {}

const TAG = 'TeamMemberCapacityTab:';
const DEFAULT_CAPACITY = 8;
const DEFAULT_BILLABLE_CAPACITY = 100;
const DEFAULT_CORPORATE_FUNCTION_CAPACITY = 0;

/**
 * Team member capacity management form component supporting create, edit, and read modes.
 * Handles capacity configuration with on-demand availability, daily hours, and time allocation.
 *
 * Features:
 * - Multi-mode operation with role-based field visibility
 * - On-demand availability toggle with conditional field hiding
 * - Available hours per day input with validation (0-24 hours)
 * - Time allocation slider for billable vs corporate function split
 * - Real-time validation and dirty state detection
 */
export const TeamMemberCapacityTab = forwardRef<
  TeamMemberCapacityTabRef,
  TeamMemberCapacityTabProps
>((props, ref) => {
  const { mode, featureData: teamMemberData, draftData, onDirectSave, onFormStateChange } = props;
  const { t } = useTranslation(['authenticated.team', 'common']);
  const { checkConflicts, isPending: isCheckingConflicts } = useCheckTeamMemberCapacityConflicts();

  /**
   * Builds initial form values based on mode and available data sources.
   * Priority: draftData (create) > teamMemberData (edit/read) > defaults (create)
   */
  const initialValues = useMemo((): TeamMemberCapacityFormData => {
    if (mode === FeatureMode.Create && draftData) {
      return {
        isOnDemandAvailability: Boolean(
          'isOnDemandAvailability' in draftData ? draftData.isOnDemandAvailability : false
        ),
        capacityPerDay: draftData.capacityPerDay ?? DEFAULT_CAPACITY,
        billableCapacity: draftData.billableCapacity ?? DEFAULT_BILLABLE_CAPACITY,
        corporateFunctionCapacity:
          draftData.corporateFunctionCapacity ?? DEFAULT_CORPORATE_FUNCTION_CAPACITY,
      };
    }

    if (mode === FeatureMode.Create) {
      return {
        isOnDemandAvailability: false,
        capacityPerDay: DEFAULT_CAPACITY,
        billableCapacity: DEFAULT_BILLABLE_CAPACITY,
        corporateFunctionCapacity: DEFAULT_CORPORATE_FUNCTION_CAPACITY,
      };
    }

    // For edit/read mode
    return {
      isOnDemandAvailability: teamMemberData?.isOnDemandAvailability ?? false,
      capacityPerDay: teamMemberData?.capacityPerDay ?? DEFAULT_CAPACITY,
      billableCapacity: teamMemberData?.billableCapacity ?? DEFAULT_BILLABLE_CAPACITY,
      corporateFunctionCapacity:
        teamMemberData?.corporateFunctionCapacity ?? DEFAULT_CORPORATE_FUNCTION_CAPACITY,
    };
  }, [mode, teamMemberData, draftData]);

  const form = useForm<TeamMemberCapacityFormData>({
    resolver: zodResolver(teamMemberCapacitySchema(t, mode === FeatureMode.Create)),
    mode: 'onChange',
    defaultValues: initialValues,
  });

  // Reset form when initialValues change (important for edit mode)
  useEffect(() => {
    form.reset(initialValues);
  }, [form, initialValues]);

  // Create adapter function to convert TeamMemberCapacityFormData to TeamMemberFormData
  const getFormDataAdapter = (): TeamMemberFormData => {
    const capacityData = form.getValues();
    // Only return Capacity tab specific fields - don't include fields from other tabs
    // This prevents overwriting data from Info/Billing/Integrations tabs in Create mode
    return {
      isOnDemandAvailability: capacityData.isOnDemandAvailability,
      capacityPerDay: capacityData.capacityPerDay,
      billableCapacity: capacityData.billableCapacity,
      corporateFunctionCapacity: capacityData.corporateFunctionCapacity,
    } as TeamMemberFormData;
  };

  // Use the tab form state hook with adapter
  const {
    isReadMode,
    handleSubmit: baseHandleSubmit,
    getFormData,
  } = useTabFormState<TeamMember, TeamMemberCapacityFormData>(props, {
    form,
    initialValues,
    isArchivedFn: member => member?.status === MemberStatus.Archived,
    triggerValidationOnMount: true,
    customDirtyStateFn: (currentValues, initialValues) => {
      if (mode === FeatureMode.Create) {
        // Create mode: Check if form is valid
        return Boolean(form.formState.isValid);
      } else {
        // Edit mode: Check if form is valid AND has changes from initial values
        if (!form.formState.isValid) {
          return false;
        }

        // Check if there are actual changes
        if (initialValues) {
          return compareFormValues(currentValues, initialValues);
        }

        // Fallback to form's built-in dirty state
        return form.formState.isDirty;
      }
    },
  });

  /**
   * Creates form values in the format expected by the conflict resolution modal
   * Uses form field names that match the conflict config keys
   */
  const getFormValuesForConflicts = (): Record<TeamMemberCapacityFieldKey, unknown> => {
    const formValues = form.getValues();
    // Normalise to 5 decimal places to eliminate floating-point drift (e.g. 33.333330000000004)
    // that causes stale watcher cycles in the billable ↔ corporate sync rules.
    const normalize = (v: number | null | undefined) => (v != null ? parseFloat(v.toFixed(5)) : v);
    return {
      isOnDemandAvailability: formValues.isOnDemandAvailability,
      capacityPerDay: formValues.capacityPerDay,
      billableCapacity: normalize(formValues.billableCapacity),
      corporateFunctionCapacity: normalize(formValues.corporateFunctionCapacity),
    };
  };

  // Use conflict resolution hook
  const {
    showConflictModal,
    conflictSnapshots,
    selectedDateRange,
    closeConflictModal,
    handleSubmitWithConflicts,
    handleResolveConflicts,
  } = useTeamMemberChangeConflictResolution<TeamMemberCapacityFormData, TeamMemberCapacityFieldKey>(
    {
      tag: TAG,
      form,
      teamMemberId: teamMemberData?.id ? String(teamMemberData.id) : undefined,
      checkConflicts,
      isCheckingConflicts,
      onFormStateChange,
      getFormData: getFormDataAdapter,
      onDirectSave,
      fields: teamMemberCapacityConflictFields,
      currentFormValues: getFormValuesForConflicts(),
    }
  );

  /**
   * Submit handler with conflict detection
   */
  const handleSubmit = async (dateRange?: SubmitDateRange): Promise<void> => {
    // Skip conflict detection for Create mode
    if (mode === FeatureMode.Create) {
      await baseHandleSubmit();
      return;
    }

    // Use the conflict resolution hook for Edit/EditChange modes
    await handleSubmitWithConflicts(baseHandleSubmit, dateRange);
  };

  // Watch isOnDemandAvailability field to conditionally show/hide fields
  const isOnDemandAvailability = useWatch({
    control: form.control,
    name: 'isOnDemandAvailability',
  });

  // Watch capacityPerDay to pass to slider
  const capacityPerDay = useWatch({
    control: form.control,
    name: 'capacityPerDay',
  });

  // Handle validation when switching on-demand mode
  useEffect(() => {
    if (isOnDemandAvailability) {
      // Clear validation errors for hidden fields and trigger validation to update form.isValid
      form.clearErrors(['capacityPerDay', 'billableCapacity', 'corporateFunctionCapacity']);
      // Trigger validation to recalculate form.isValid state based on schema refinements
      void form.trigger();
    } else {
      // When switching back from on-demand mode, trigger validation for all capacity fields
      void form.trigger(['capacityPerDay', 'billableCapacity', 'corporateFunctionCapacity']);
    }
  }, [isOnDemandAvailability, form]);

  // Snapshot form values keyed to the current set of conflict snapshots.
  // conflictSnapshots only changes when new conflicts are detected (i.e. when
  // the modal is about to open), so this memo is stable while the modal is open
  // and prevents useConflictResolution from resetting its internal form on
  // every parent re-render.
  const conflictFormValues = useMemo(
    () => getFormValuesForConflicts(),
    [conflictSnapshots] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const conflictModalSyncRules = useTeamMemberCapacitySyncRules(getFormValuesForConflicts);

  const tabContent = (
    <Form {...form}>
      <form className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('details.tabs.capacity.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ToggleWithDescription
              name="isOnDemandAvailability"
              control={form.control}
              label={t('details.tabs.capacity.onDemand')}
              description={t('details.tabs.capacity.onDemandDescription')}
              disabled={isReadMode}
            />

            {!isOnDemandAvailability && (
              <>
                <InputFieldWithCurrency
                  name="capacityPerDay"
                  control={form.control}
                  label={t('details.tabs.capacity.availableHoursPerDay')}
                  placeholder="8"
                  suffixText="h"
                  isReadMode={isReadMode}
                  maxDecimalPlaces={0}
                  max={24}
                />

                <DoubleSidedSlider
                  leftFieldName="billableCapacity"
                  rightFieldName="corporateFunctionCapacity"
                  control={form.control}
                  label={t('details.tabs.capacity.timeAllocation')}
                  description={t('details.tabs.capacity.timeAllocationDescription')}
                  leftLabelKey="authenticated.team:details.tabs.capacity.billable"
                  rightLabelKey="authenticated.team:details.tabs.capacity.corporateFunction"
                  disabled={isReadMode}
                  totalValue={capacityPerDay || 0}
                />
              </>
            )}
          </CardContent>
        </Card>
      </form>
    </Form>
  );

  return (
    <>
      <BaseFeatureTab
        {...props}
        ref={ref}
        isArchivedFn={member => member?.status === MemberStatus.Archived}
        onSubmit={handleSubmit}
        isValid={form.formState.isValid}
        getFormData={getFormDataAdapter}
        isCheckingConflicts={isCheckingConflicts}
      >
        {tabContent}
      </BaseFeatureTab>

      {/* Conflict Resolution Modal */}
      {showConflictModal && selectedDateRange && (
        <ResolveConflictsModal
          isOpen={showConflictModal}
          onClose={closeConflictModal}
          snapshots={conflictSnapshots}
          currentFormValues={conflictFormValues}
          selectedDateRange={selectedDateRange}
          fields={teamMemberCapacityConflictFields}
          onResolve={handleResolveConflicts}
          t={t}
          translationNamespace="authenticated.team"
          fieldSyncRules={conflictModalSyncRules}
        />
      )}
    </>
  );
});
